import { BreakPointDescDTO, BreakPointDTO, TripDTO } from '@/types/controlsheet'
import { useTable } from '@/ui/Table'
import { ControlSheetData } from '../api/useGetControlSheets'
import { useControlSheetColumns } from './useControlSheetColumns'
import { ControlSheetAnnotation, ControlSheetNote, ControlSheetRow, ControlSheetTrip } from '../types/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { annotationMapper } from '../helpers/annotationMapper'
import { postprocessRun } from '../helpers/postprocessRun'
import { tripRowSorter } from '../helpers/controlSheetSorter'
import { breakpointMapper } from './breakpointMapper'
import { isTimeSameOrAfter } from '../helpers/breakpointTimeUtils'
import { standardizeTime } from '../helpers/standardizeTime'
import { spellTimeForTrip } from '../helpers/spellTimeForTrip'
import { convertMinutesStrToHoursMinutes } from '../helpers/convertMinutesToHoursMinutes'
import { rowId } from '../helpers/rowId'
import { tripsAndDutiesAffectedByLateMealRelief } from '../helpers/tripsAffectedByLateMealRelief'
import { AnnotationsPermission } from '@/pages/ControlSheet/ui/ControlSheetPage'
import { identifyOppositeTrips } from '../helpers/identifyOppositeTrips'
import { pairTripsProperly, PairTripsResult } from '../helpers/pairTripsProperly'

export type AllBreakpoints = {
    outbound: BreakPointDescDTO[]
    inbound: BreakPointDescDTO[]
}

const organiseBreakpoints = (
    breakPoints: (BreakPointDTO | null)[],
    routeBreakpoints: BreakPointDescDTO[]
): (BreakPointDTO | null)[] => {
    if (breakPoints.length <= 1) {
        return breakPoints
    }

    const deduplicated: (BreakPointDTO | null)[] = [breakPoints[0]]

    for (let i = 1; i < breakPoints.length; i++) {
        if (breakPoints[i] !== null && breakPoints[i - 1] !== null) {
            if (breakPoints[i]?.stopId !== breakPoints[i - 1]?.stopId) {
                deduplicated.push(breakPoints[i])
            }
        }
    }

    const result: (BreakPointDTO | null)[] = []
    routeBreakpoints.forEach((routeBreakpoint) => {
        const bp = deduplicated.find((b) => b !== null && b.stopId === routeBreakpoint.stopId)
        if (bp) {
            result.push(bp)
        } else {
            result.push(null)
        }
    })
    return result
}

const transformData =
    (
        mealRelief: number,
        tempNotes: ControlSheetNote[],
        tempDeleteNotes: string[],
        tempAnnotations: ControlSheetAnnotation[],
        tripsAndDutiesAffectedByMealRelief: { trip: string; duty: string }[],
        breakpoints: AllBreakpoints
    ) =>
    ({ run, nextTripMap }: PairTripsResult): ControlSheetRow[] => {
        const transformTrip = (
            trip: TripDTO,
            colspan: { value: number },
            direction: 'inbound' | 'outbound'
        ): ControlSheetTrip => {
            const nextTrip = nextTripMap.get(trip)
            const spellTime = trip.spellTime ? parseInt(trip.spellTime, 10) : spellTimeForTrip(trip)
            const nearDrivingHours = spellTime > 4.5 * 60

            const tempAnnotationsForTrip = tempAnnotations
                .filter((a) => a.runNumber === run.run && a.tripNumber === trip.trip && a.dutyCode === trip.duty)
                .map((annotation) => {
                    const notes = [
                        ...annotation.notes,
                        ...tempNotes.filter((note) => note.annotationId === annotation.id),
                    ]
                    const uniqueNotes = Array.from(new Map(notes.map((item) => [item.id, item])).values())
                    return {
                        ...annotation,
                        notes: uniqueNotes,
                    }
                })

            const breakpointsArrayContainsArrivalStand = trip.breakPoints.some(
                (bp) => bp?.stopId === trip.arriveStandCode
            )

            const tripBreakpoints = [...trip.breakPoints]
            if (!breakpointsArrayContainsArrivalStand && trip.arriveStandCode) {
                tripBreakpoints.push({
                    stopId: trip.arriveStandCode,
                    time: trip.arriveStandTime,
                    name: '',
                })
            }

            const organisedBreakpoints = organiseBreakpoints(
                tripBreakpoints,
                direction === 'outbound' ? breakpoints.outbound : breakpoints.inbound
            )

            const lateMealReliefMayAffect = tripsAndDutiesAffectedByMealRelief.some(
                (tripInfo) => tripInfo.trip === trip.trip && tripInfo.duty === trip.duty
            )

            const mappedOrganisedBreakpoints = organisedBreakpoints.map(
                breakpointMapper(
                    mealRelief,
                    colspan,
                    trip,
                    organisedBreakpoints,
                    nearDrivingHours,
                    trip.departGarage,
                    tempAnnotationsForTrip,
                    direction === 'outbound' ? breakpoints.outbound : breakpoints.inbound
                )
            )

            return {
                trip: trip.trip,
                depotCode: trip.depotCode,
                depotGarage: trip.depotGarage,
                departStandTime: standardizeTime(trip.departStandTime),
                arriveStandTime: standardizeTime(trip.arriveStandTime),
                duty: trip.duty,
                breakpoints: mappedOrganisedBreakpoints,
                layover: colspan.value > 0 ? 'SKIP_CELL' : trip.layover ? String(trip.layover) : '',
                breakStart: standardizeTime(trip.breakStartTime),
                breakEnd: standardizeTime(trip.breakEndTime),
                shiftStart: standardizeTime(trip.shiftStartTime),
                shiftEnd: standardizeTime(trip.shiftEndTime),
                spellTime,
                departGarage: trip.departGarage,
                isNearDrivingHours: nearDrivingHours,
                reliefTrip: trip.reliefTrip,
                reliefStartTime: trip.breakStartTime,
                reliefTimeMins: trip.mealReliefTime ?? '',
                reliefEndTime: trip.breakEndTime,
                lastTrip: trip.lastTrip,
                currentSpell: trip.spell,
                dutySpells: trip.dutySpells,
                timeOnDuty: convertMinutesStrToHoursMinutes(trip.timeOnDuty),
                nextTripForDuty: trip.nextTripForDuty ? String(trip.nextTripForDuty) : '',
                previousTripForDuty: trip.previousTripForDuty ? String(trip.previousTripForDuty) : null,
                nextRoute: trip.nextRoute ?? '',
                previousRoute: trip.previousRoute ?? null,
                code: trip.code,
                annotations: [
                    ...tempAnnotationsForTrip,
                    ...trip.annotations
                        .filter((annotation) => !tempDeleteNotes.includes(annotation.id))
                        .map(annotationMapper(tempNotes)),
                ],
                lateMealReliefMayAffect: lateMealReliefMayAffect,
                previousTripBreakInfo: trip.previousTripBreakInfo
                    ? {
                          trip: trip.previousTripBreakInfo.trip,
                          run: trip.previousTripBreakInfo.run,
                          route: trip.previousTripBreakInfo.route,
                          mealReliefAnnotations:
                              trip.previousTripBreakInfo.mealReliefAnnotations.map(annotationMapper()),
                      }
                    : null,
                tripBeforeBreak: trip.tripBeforeBreak,
                tripAfterBreak: trip.tripAfterBreak,
                routeAfterBreak: trip.routeAfterBreak,
                runAfterBreak: trip.runAfterBreak,
                totalDrivingMinutesOnDuty: trip.totalDrivingMinutesOnDuty || 0,
                drivingStartTime: standardizeTime(trip.vStartTime),
                drivingEndTime: standardizeTime(trip.vEndTime),
                runNextDuty: nextTrip?.duty,
                runNextTrip: nextTrip?.trip,
            }
        }

        return run.tripPairs.map((tripPair): ControlSheetRow => {
            const outboundColspan = { value: 0 }
            const inboundColspan = { value: 0 }

            return {
                run: run.run,
                outbound: tripPair.outbound ? transformTrip(tripPair.outbound, outboundColspan, 'outbound') : undefined,
                inbound: tripPair.inbound ? transformTrip(tripPair.inbound, inboundColspan, 'inbound') : undefined,
            }
        })
    }

const isChangeoverMealRelief = (trip: ControlSheetTrip): boolean => {
    const code = trip.code
    if (code === 'F' || code === 'FX') {
        if (!trip.shiftEnd || !trip.arriveStandTime) return false
        return isTimeSameOrAfter(trip.shiftEnd, trip.arriveStandTime)
    }
    return false
}

const emptyTakeoverTrip = (overrides: Partial<ControlSheetTrip>): ControlSheetTrip => ({
    trip: '',
    depotCode: '',
    depotGarage: '',
    departStandTime: '',
    arriveStandTime: '',
    duty: '',
    breakpoints: [],
    layover: null,
    breakStart: '',
    breakEnd: '',
    shiftStart: '',
    shiftEnd: '',
    spellTime: 0,
    departGarage: false,
    isNearDrivingHours: false,
    reliefTrip: null,
    reliefStartTime: '',
    reliefTimeMins: '',
    reliefEndTime: '',
    lastTrip: '',
    currentSpell: '',
    dutySpells: '',
    timeOnDuty: '',
    nextTripForDuty: '',
    nextRoute: '',
    code: null,
    annotations: [],
    lateMealReliefMayAffect: false,
    previousTripBreakInfo: null,
    tripAfterBreak: null,
    tripBeforeBreak: null,
    routeAfterBreak: null,
    runAfterBreak: null,
    totalDrivingMinutesOnDuty: 0,
    drivingStartTime: null,
    drivingEndTime: null,
    runNextDuty: '',
    runNextTrip: '',
    isTakeoverRow: true,
    previousTripForDuty: null,
    previousRoute: null,
    ...overrides,
})

const createTakeoverRow = (
    runNumber: string,
    direction: 'outbound' | 'inbound',
    trip: ControlSheetTrip
): ControlSheetRow => {
    if (direction === 'outbound') {
        return {
            run: runNumber,
            outbound: emptyTakeoverTrip({ duty: trip.runNextDuty }),
            inbound: emptyTakeoverTrip({ trip: trip.runNextTrip }),
        }
    } else {
        return {
            run: runNumber,
            outbound: emptyTakeoverTrip({}),
            inbound: emptyTakeoverTrip({
                duty: trip.runNextDuty,
                trip: trip.runNextTrip,
            }),
        }
    }
}

const insertTakeoverRows = (rows: ControlSheetRow[]): void => {
    for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i]

        if (row.inbound && isChangeoverMealRelief(row.inbound) && row.inbound.runNextTrip) {
            // Suppress the takeover row if the next-trip inbound already appears as
            // a real (non-placeholder) row further down — avoids duplicates when
            // pairTripsProperly has already surfaced the taking-over duty as its own row.
            const nextTrip = row.inbound.runNextTrip
            const nextDuty = row.inbound.runNextDuty
            const alreadyPresent = rows.some(
                (r, idx) =>
                    idx > i &&
                    r.inbound?.trip === nextTrip &&
                    !r.inbound?.isTakeoverRow &&
                    (!nextDuty || r.inbound?.duty === nextDuty)
            )
            if (!alreadyPresent) {
                rows.splice(i + 1, 0, createTakeoverRow(row.run, 'inbound', row.inbound))
            }
        }

        // R/RX outbound with a different-duty inbound: detach the inbound to a new row below
        // so the existing R/RX no-inbound column logic renders the next-trip/arrow/route display.
        if (
            row.outbound &&
            (row.outbound.code === 'R' || row.outbound.code === 'RX') &&
            row.inbound &&
            row.outbound.duty !== row.inbound.duty
        ) {
            const detachedInbound = row.inbound
            row.inbound = undefined
            rows.splice(i + 1, 0, { run: row.run, inbound: detachedInbound })
        }
    }
}

export const useControlSheetTable = (
    route: string | undefined,
    annotationsPermission: AnnotationsPermission,
    controlSheetData: ControlSheetData,
    navigateToTrip: (trip: string) => void
) => {
    // tempNotes and tempAnnotations store any annotations the user creates without having to
    // refresh from the API
    const [tempNotes, setTempNotes] = useState<Record<string, ControlSheetNote[]>>({})
    const [tempDeleteNotes, setTempDeleteNotes] = useState<Record<string, string[]>>({})
    const [tempAnnotations, setTempAnnotations] = useState<Record<string, ControlSheetAnnotation[]>>({})
    const [lastDataUpdate, setLastDataUpdate] = useState<Record<string, number>>({})

    const { data: controlSheets, isLoading: isCSLoading, refetch, dataUpdatedAt } = controlSheetData

    const theRoute = route!

    // If the actual endpoint updates, reset all temporary notes and annotations
    useEffect(() => {
        if (dataUpdatedAt && dataUpdatedAt > lastDataUpdate[theRoute]) {
            setTempAnnotations((prev) => {
                const newState = { ...prev }
                delete newState[theRoute]
                return newState
            })
            setTempNotes((prev) => {
                const newState = { ...prev }
                delete newState[theRoute]
                return newState
            })
            setTempDeleteNotes((prev) => {
                const newState = { ...prev }
                delete newState[theRoute]
                return newState
            })
            setLastDataUpdate((prev) => ({ ...prev, [theRoute]: dataUpdatedAt }))
        }
    }, [dataUpdatedAt, lastDataUpdate, theRoute])

    const firstControlSheet = controlSheets && controlSheets.find((cs) => cs.route === theRoute)

    const breakpoints = useMemo(() => {
        return firstControlSheet
            ? {
                  outbound: firstControlSheet?.outboundBreakpoints || [],
                  inbound: firstControlSheet?.inboundBreakpoints || [],
              }
            : { outbound: [], inbound: [] }
    }, [firstControlSheet])

    const addTempNote = useCallback(
        (note: ControlSheetNote) => {
            setTempNotes((prev) => {
                const existingNotes = prev[theRoute] || []
                return {
                    ...prev,
                    [theRoute]: [...existingNotes, note],
                }
            })
        },
        [theRoute]
    )

    const addTempAnnotation = useCallback(
        (annotation: ControlSheetAnnotation) => {
            const noteIds = annotation.notes.map((note) => note.annotationId)
            const filteredNotes = (tempNotes[theRoute] || []).filter((note) => !noteIds.includes(note.annotationId))
            setTempNotes((prev) => ({
                ...prev,
                [theRoute]: filteredNotes,
            }))
            setTempAnnotations((prev) => {
                const existingAnnotations = prev[theRoute] || []
                return {
                    ...prev,
                    [theRoute]: [
                        ...existingAnnotations.filter((existingAnnotation) => existingAnnotation.id !== annotation.id),
                        annotation,
                    ],
                }
            })
        },
        [tempNotes, theRoute]
    )

    const deleteTempNote = useCallback(
        (noteId: string) => {
            setTempDeleteNotes((prev) => {
                const existingDeleteNotes = prev[theRoute] || []
                return {
                    ...prev,
                    [theRoute]: [...existingDeleteNotes, noteId],
                }
            })
            setTempNotes((prev) => {
                const existingNotes = prev[theRoute] || []
                return {
                    ...prev,
                    [theRoute]: existingNotes.filter((note) => note.id !== noteId),
                }
            })
        },
        [theRoute]
    )

    const inboundTripNumForLowestOutbound = useMemo(() => {
        if (!firstControlSheet) {
            return 0
        }
        let lowestOutbound = Number.MAX_SAFE_INTEGER
        let inboundForLowestOutbound = -1
        firstControlSheet.runs.forEach((run) => {
            run.tripPairs.forEach((pair) => {
                if (pair.outbound && pair.inbound) {
                    const tripNum = parseInt(pair.outbound.trip, 10)
                    if (!isNaN(tripNum) && tripNum < lowestOutbound) {
                        lowestOutbound = tripNum
                        inboundForLowestOutbound = pair.inbound?.trip ? parseInt(pair.inbound.trip, 10) : -1
                    }
                }
            })
        })
        return inboundForLowestOutbound
    }, [firstControlSheet])

    const columns = useControlSheetColumns(
        route,
        breakpoints,
        isCSLoading,
        firstControlSheet?.mealRelief || 0,
        annotationsPermission,
        inboundTripNumForLowestOutbound,
        refetch,
        addTempNote,
        deleteTempNote,
        addTempAnnotation,
        navigateToTrip
    )

    const tableConfig = useMemo(() => {
        const tripsAndDutiesAffectedByMealRelief = tripsAndDutiesAffectedByLateMealRelief(
            firstControlSheet,
            tempAnnotations[theRoute] || []
        )

        const transformedData =
            firstControlSheet?.runs
                .map(pairTripsProperly)
                .flatMap(
                    transformData(
                        firstControlSheet?.mealRelief,
                        tempNotes[theRoute] || [],
                        tempDeleteNotes[theRoute] || [],
                        tempAnnotations[theRoute] || [],
                        tripsAndDutiesAffectedByMealRelief,
                        breakpoints
                    )
                ) || []

        const sorted = [...transformedData].sort(tripRowSorter(inboundTripNumForLowestOutbound))

        const iterations = 2

        if (sorted.length > 0) {
            for (let i = 0; i < iterations; i++) {
                identifyOppositeTrips(sorted)
                sorted.sort(tripRowSorter(inboundTripNumForLowestOutbound))
                // Matchup duties and postprocess the run to ensure meal reliefs are correct
                postprocessRun(sorted)
            }
        }

        insertTakeoverRows(sorted)

        return {
            data: sorted,
            columns,
            initialState: {
                sorting: [
                    {
                        id: 'outbound-trip',
                        desc: false,
                    },
                ],
            },
            getRowId: (row: ControlSheetRow) => {
                return `${theRoute}-${rowId(row)}`
            },
        }
    }, [
        firstControlSheet,
        tempAnnotations,
        theRoute,
        tempNotes,
        tempDeleteNotes,
        inboundTripNumForLowestOutbound,
        columns,
        breakpoints,
    ])

    const table = useTable(tableConfig)

    return {
        table,
        timetable: firstControlSheet?.timetable,
        mealRelief: firstControlSheet?.mealRelief,
        refetch,
    }
}
